import axios from "axios"
import { pnApiBaseUri } from "./constants"
import { ListingParameters, ListingResponseDTO } from "./dto/shared"
import { CreatePresetRequestDTO, PresetDTO, UpdatePresetRequestDTO } from "./dto/presets"
import { CreateProjectRequestDTO, ProjectDTO, UpdateProjectRequestDTO } from "./dto/projects"
import {
	GetSignedUrlsResponseDTO,
	SignedUrlDTO,
	SignedUrlRequestOptions,
	UploadContentResponseDTO,
} from "./dto/upload"
import {
	FetchActivitiesResponseBodyDTO,
	GetUserResponseBody,
	GetAppConfigBody,
	UpdateUserRequestBody,
} from "./dto/users"
import { MigrateAccountResponse } from "./dto/migration"
import { GetServiceStatusResponseBody } from "./dto/serviceStatus"
import {
	GetPaymentPlansResponseBody,
	PostCreditsConsumptionEventsRequestBody,
	PostStripeCheckoutSessionRequestBody,
	PostStripeSessionResponseBody,
	RedeemCodeRequestBody,
	RedeemCodeResponseBody,
} from "./dto/payment"
import { registerProjectIdAsDeleted, removeProjectIdAsDeleted } from "@/api/supression"
import { CreateImageRequestDTO, ImageDTO } from "./dto/images"
import {
	CreateStyleRequestDTO,
	DuplicateStyleRequestDTO,
	AIStyleCategoryDTO,
	StyleResponseDTO,
	StyleVisibility,
	UpdateStyleRequestDTO,
} from "./dto/styles"
import { UpsertStyleReferenceRequestDTO, StyleReferenceResponseDTO } from "./dto/style-references"
import {
	transformBinaryForSerialization,
	transformBinaryFromSerialization,
} from "./dto/binary-serialization"
import { attachAxiosNetworkBehavior, type NetworkRequestIntent } from "./network-behavior"
import { setMaintenanceModeFromAPIResponse } from "@/store/maintenance-mode"
import { pixiesetAssetBaseURIs } from "@/utils/pixieset-urls"

export const axiosClient = axios.create()
attachAxiosNetworkBehavior(axiosClient)

// Add request interceptor to serialize binary data
axiosClient.interceptors.request.use((config) => {
	if (
		config.data &&
		typeof config.data === "object" &&
		!(config.data instanceof FormData) &&
		!(config.data instanceof Blob)
	) {
		// This converts Float32Array/Uint8Array to {__binaryType__, __binaryData__}
		config.data = transformBinaryForSerialization(config.data)
	}
	return config
})

// Add response interceptor to deserialize binary data and check for active maintenance
axiosClient.interceptors.response.use(
	(response) => {
		if (response.data && typeof response.data === "object") {
			// This converts {__binaryType__, __binaryData__} back to Float32Array/Uint8Array
			response.data = transformBinaryFromSerialization(response.data)
		}
		return response
	},
	(error: unknown) => {
		if (axios.isAxiosError(error) && error.response?.status === 503) {
			setMaintenanceModeFromAPIResponse(error.response.data)
		}

		return Promise.reject(error)
	},
)

export class PNApiOperations {
	static readonly basePaths = {
		payment: `${pnApiBaseUri}/payment`,
		presets: `${pnApiBaseUri}/presets`,
		projects: `${pnApiBaseUri}/projects`,
		images: `${pnApiBaseUri}/images`,
		serviceStatus: `${pnApiBaseUri}/service-status`,
		signedUrls: `${pnApiBaseUri}/upload/signed-urls`,
		users: `${pnApiBaseUri}/iam/users`,
		styles: `${pnApiBaseUri}/styles`,
		rpc: `${pnApiBaseUri}/rpc`,
		management: `${pnApiBaseUri}/management`,
		appConfig: `${pnApiBaseUri}/app-config`,
	}

	static readonly listImages = async (
		listingParameters?: ListingParameters,
	): Promise<ListingResponseDTO<ImageDTO>> => {
		const response = await axiosClient.get<ListingResponseDTO<ImageDTO>>(
			this.basePaths.images,
			{
				params: listingParameters,
			},
		)
		return response.data
	}

	static readonly getImage = async (imagetId: string): Promise<ImageDTO> => {
		const response = await axiosClient.get<ImageDTO>(`${this.basePaths.images}/${imagetId}`)
		return response.data
	}

	static readonly createImage = async (body: CreateImageRequestDTO): Promise<ImageDTO> => {
		const response = await axiosClient.post<ImageDTO>(this.basePaths.images, body)
		return response.data
	}

	static readonly deleteImage = async (imageId: string): Promise<void> => {
		await axiosClient.delete(`${this.basePaths.images}/${imageId}`)
	}

	static readonly listPresets = async (
		listingParameters?: ListingParameters,
	): Promise<ListingResponseDTO<PresetDTO>> => {
		const response = await axiosClient.get<ListingResponseDTO<PresetDTO>>(
			this.basePaths.presets,
			{
				params: listingParameters,
			},
		)
		return response.data
	}

	static readonly getPreset = async (presetId: string): Promise<PresetDTO> => {
		const response = await axiosClient.get<PresetDTO>(`${this.basePaths.presets}/${presetId}`)
		return response.data
	}

	static readonly createPreset = async (body: CreatePresetRequestDTO): Promise<PresetDTO> => {
		const response = await axiosClient.post<PresetDTO>(this.basePaths.presets, body)
		return response.data
	}

	static readonly updatePreset = async (
		presetId: string,
		body: UpdatePresetRequestDTO,
	): Promise<PresetDTO> => {
		const response = await axiosClient.patch<PresetDTO>(
			`${this.basePaths.presets}/${presetId}`,
			body,
		)
		return response.data
	}

	static readonly deletePreset = async (presetId: string): Promise<void> => {
		await axiosClient.delete(`${this.basePaths.presets}/${presetId}`)
	}

	static readonly listProjects = async (
		listingParameters?: ListingParameters,
	): Promise<ListingResponseDTO<ProjectDTO>> => {
		const response = await axiosClient.get<ListingResponseDTO<ProjectDTO>>(
			this.basePaths.projects,
			{
				params: listingParameters,
			},
		)
		return response.data
	}

	static readonly getProject = async (projectId: string): Promise<ProjectDTO> => {
		const response = await axiosClient.get<ProjectDTO>(
			`${this.basePaths.projects}/${projectId}`,
		)
		return response.data
	}

	static readonly createProject = async (body: CreateProjectRequestDTO): Promise<ProjectDTO> => {
		const response = await axiosClient.post<ProjectDTO>(this.basePaths.projects, body)
		removeProjectIdAsDeleted(response.data.projectId)
		return response.data
	}

	static readonly updateProject = async (
		projectId: string,
		body: UpdateProjectRequestDTO,
	): Promise<ProjectDTO> => {
		const response = await axiosClient.patch<ProjectDTO>(
			`${this.basePaths.projects}/${projectId}`,
			body,
		)
		return response.data
	}

	static readonly deleteProject = async (projectId: string): Promise<void> => {
		await axiosClient.delete(`${this.basePaths.projects}/${projectId}`)
		registerProjectIdAsDeleted(projectId)
	}

	static readonly getSignedUrls = async (
		opts: SignedUrlRequestOptions[],
		options?: { networkIntent?: NetworkRequestIntent },
	): Promise<SignedUrlDTO[]> => {
		const params = {
			"content-types": opts.map((o) => o.contentType).join(","),
			"entity-types": opts.map((o) => o.entityType).join(","),
			"content-digests": opts.map((o) => o.contentDigest ?? "").join(","),
		}
		const response = await axiosClient.get<GetSignedUrlsResponseDTO>(
			this.basePaths.signedUrls,
			{
				params,
				networkIntent: options?.networkIntent ?? "active",
			},
		)
		return response.data.urls
	}

	static readonly getCurrentUser = async (): Promise<GetUserResponseBody> => {
		const res = await axiosClient.get<GetUserResponseBody>(`${this.basePaths.users}/@me`)
		return res.data
	}

	static readonly getAppConfig = async (signal?: AbortSignal): Promise<GetAppConfigBody> => {
		const res = await axiosClient.get<{ data: GetAppConfigBody }>(
			`${this.basePaths.appConfig}`,
			{
				signal,
			},
		)
		return res.data.data
	}

	static readonly updateCurrentUser = async (
		body: UpdateUserRequestBody,
		networkIntent: NetworkRequestIntent,
	): Promise<void> => {
		await axiosClient.patch(`${this.basePaths.users}/@me`, body, { networkIntent })
	}

	static readonly syncUserProfile = async (
		networkIntent: NetworkRequestIntent,
	): Promise<void> => {
		await axiosClient.post(`${this.basePaths.rpc}/sync-user-profile`, undefined, {
			networkIntent,
		})
	}

	static readonly migrateAccount = async (
		sourceToken: string,
	): Promise<MigrateAccountResponse> => {
		const res = await axiosClient.post<MigrateAccountResponse>(
			`${this.basePaths.rpc}/migrate-account-data`,
			{ sourceToken },
		)
		return res.data
	}

	static readonly deleteCurrentUser = async (): Promise<boolean> => {
		const res = await axiosClient.delete(`${this.basePaths.users}/@me`)
		return res.status === 200 || res.status === 202 || res.status === 204
	}

	static readonly getUserActivities = async (
		params: ListingParameters,
	): Promise<FetchActivitiesResponseBodyDTO> => {
		const url = `${this.basePaths.users}/@me/activity-events`
		const res = await axiosClient.get<FetchActivitiesResponseBodyDTO>(url, { params })
		return res.data
	}

	static readonly getServiceStatus = async (): Promise<GetServiceStatusResponseBody> => {
		const res = await axiosClient.get<GetServiceStatusResponseBody>(
			this.basePaths.serviceStatus,
			{
				networkIntent: "background",
			},
		)
		return res.data
	}

	static readonly getPaymentPlans = async (): Promise<GetPaymentPlansResponseBody> => {
		const res = await axiosClient.get<GetPaymentPlansResponseBody>(
			`${this.basePaths.payment}/plans`,
			{
				networkIntent: "background",
			},
		)
		return res.data
	}

	static readonly createStripeCheckoutSession = async (
		body: PostStripeCheckoutSessionRequestBody,
	): Promise<PostStripeSessionResponseBody> => {
		const res = await axiosClient.post<PostStripeSessionResponseBody>(
			`${this.basePaths.payment}/stripe/checkout-sessions`,
			body,
		)
		return res.data
	}

	static readonly createStripeCustomerPortalSession =
		async (): Promise<PostStripeSessionResponseBody> => {
			const res = await axiosClient.post<PostStripeSessionResponseBody>(
				`${this.basePaths.payment}/stripe/customer-portal-sessions`,
			)
			return res.data
		}

	static readonly redeemPromoCode = async (
		body: RedeemCodeRequestBody,
	): Promise<RedeemCodeResponseBody> => {
		const res = await axiosClient.post<RedeemCodeResponseBody>(
			`${this.basePaths.payment}/promo-codes/redemptions`,
			body,
		)
		return res.data
	}

	static readonly reportCreditConsumptionEvents = async (
		body: PostCreditsConsumptionEventsRequestBody,
	): Promise<void> => {
		await axiosClient.post(`${this.basePaths.payment}/credit-consumption/events`, body)
	}

	static readonly reportGenAiCreditConsumptionEvents = async (
		body: PostCreditsConsumptionEventsRequestBody,
	): Promise<void> => {
		await axiosClient.post(`${this.basePaths.payment}/gen-ai-credit-consumption/events`, body)
	}

	static readonly uploadContent = async (options: {
		contentType?: string
		data: Blob
		url: string
		networkIntent?: NetworkRequestIntent
	}): Promise<UploadContentResponseDTO> => {
		if (!(options.data instanceof Blob)) {
			throw new Error(
				`[uploadContent] Invalid upload payload for ${options.url}: expected Blob`,
			)
		}
		if (options.data.size === 0) {
			throw new Error(`[uploadContent] Invalid upload payload for ${options.url}: empty Blob`)
		}
		const contentType = options.contentType || options.data.type || "application/octet-stream"

		const response = await axiosClient.put<UploadContentResponseDTO>(
			options.url,
			options.data,
			{
				networkIntent: options.networkIntent,
				transformRequest: <T>(data: T, headers: Record<string, string>) => {
					delete headers["Authorization"]
					headers["Content-Type"] = contentType
					return data
				},
			},
		)
		return { url: this.convertToCDNUrl(response.data.url) }
	}

	static readonly convertToCDNUrl = (url: string): string => {
		const urlObject = new URL(url)

		if (urlObject.host === "polarr-playground-images.s3.us-west-1.amazonaws.com") {
			urlObject.host = new URL(pixiesetAssetBaseURIs.imagesPe).host
		}
		return urlObject.toString()
	}

	/**
	 * Style endpoints
	 */
	static readonly createStyle = async (
		style: CreateStyleRequestDTO,
	): Promise<StyleResponseDTO> => {
		const response = await axiosClient.post<StyleResponseDTO>(this.basePaths.styles, style)
		return response.data
	}

	static readonly updateStyle = async (
		style: UpdateStyleRequestDTO,
	): Promise<StyleResponseDTO> => {
		const response = await axiosClient.patch<StyleResponseDTO>(
			`${this.basePaths.styles}/${style.styleId}`,
			style,
		)
		return response.data
	}

	static readonly getStyles = async (
		listingParameters?: ListingParameters,
	): Promise<ListingResponseDTO<StyleResponseDTO>> => {
		const response = await axiosClient.get<ListingResponseDTO<StyleResponseDTO>>(
			this.basePaths.styles,
			{
				params: listingParameters,
			},
		)
		return response.data
	}

	static readonly getInternalStyles = async (): Promise<ListingResponseDTO<StyleResponseDTO>> => {
		const response = await axiosClient.get<ListingResponseDTO<StyleResponseDTO>>(
			`${this.basePaths.management}/styles`,
			{
				params: { visibility: "internal" },
			},
		)
		return response.data
	}

	static readonly getStyleCategories = async (
		visibility?: StyleVisibility | StyleVisibility[],
	): Promise<ListingResponseDTO<AIStyleCategoryDTO>> => {
		const baseUrl = `${this.basePaths.styles}/categories`
		const url =
			Array.isArray(visibility) && visibility.length > 0
				? `${baseUrl}?${visibility
						.map((v) => `visibility=${encodeURIComponent(v)}`)
						.join("&")}`
				: baseUrl

		const response = await axiosClient.get<ListingResponseDTO<AIStyleCategoryDTO>>(
			url,
			Array.isArray(visibility) || !visibility
				? undefined
				: {
						params: { visibility },
					},
		)
		return response.data
	}

	static readonly getCategoryStyles = async (
		categoryId: string,
	): Promise<ListingResponseDTO<StyleResponseDTO>> => {
		const response = await axiosClient.get<ListingResponseDTO<StyleResponseDTO>>(
			`${this.basePaths.styles}/categories/${categoryId}/styles`,
		)
		return response.data
	}

	static readonly getStyle = async (
		styleId: string,
		networkIntent: NetworkRequestIntent = "background",
	): Promise<StyleResponseDTO> => {
		const response = await axiosClient.get<StyleResponseDTO>(
			`${this.basePaths.styles}/${styleId}`,
			{
				networkIntent,
			},
		)
		return response.data
	}

	static readonly deleteStyle = async (styleId: string): Promise<void> => {
		await axiosClient.delete(`${this.basePaths.styles}/${styleId}`)
	}

	static readonly duplicateStyle = async (
		request: DuplicateStyleRequestDTO,
	): Promise<StyleResponseDTO> => {
		const response = await axiosClient.post<StyleResponseDTO>(
			`${this.basePaths.rpc}/duplicate-style`,
			request,
		)
		return response.data
	}

	/**
	 * Style reference endpoints
	 */
	static readonly createOrUpdateStyleReference = async (
		styleId: string,
		styleReference: UpsertStyleReferenceRequestDTO,
	): Promise<StyleReferenceResponseDTO> => {
		const response = await axiosClient.put<StyleReferenceResponseDTO>(
			`${this.basePaths.styles}/${styleId}/references/${encodeURIComponent(
				styleReference.imageId,
			)}`,
			styleReference,
		)
		return response.data
	}

	static readonly deleteStyleReference = async (
		styleId: string,
		imageId: string,
	): Promise<void> => {
		await axiosClient.delete<StyleReferenceResponseDTO>(
			`${this.basePaths.styles}/${styleId}/references/${encodeURIComponent(imageId)}`,
		)
	}

	static readonly getStyleReferences = async (
		styleId: string,
	): Promise<ListingResponseDTO<StyleReferenceResponseDTO>> => {
		const response = await axiosClient.get<ListingResponseDTO<StyleReferenceResponseDTO>>(
			`${this.basePaths.styles}/${styleId}/references`,
			{
				networkIntent: "active",
			},
		)
		return response.data
	}
}
